import { Input } from 'commons/components';
import { PocIconButton } from 'commons/components/components-wrapper/poc-icon-button.component';
import { useTranslation } from 'core/i18n/i18n';
import { RangeMode, ResultType } from 'modules/settings/pods/settings-materials/material-definition.vm';
import {
    QcLevelEditableField,
    QcLevelFormItem,
} from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.model';
import { QcLevelLineLayout, QcLevelRangeInputs, QcLevelTargetValues } from './qc-level-line.style';

const QC_CONFIG_KEY = 'settings.materials.material-definitions.form.qc-config';

interface QcLevelLineProps {
    level: QcLevelFormItem;
    index: number;
    rowKey: string;
    resultType: ResultType;
    rangeMode: RangeMode;
    errorKey?: string;
    onChange: (field: QcLevelEditableField, value: string | null) => void;
    onRemove: VoidFunction;
}

const toInputValue = (value?: number): string => (value == null ? '' : String(value));

export const QcLevelLine: React.FC<QcLevelLineProps> = ({
    level,
    index,
    rowKey,
    resultType,
    rangeMode,
    errorKey,
    onChange,
    onRemove,
}) => {
    const { t } = useTranslation();
    const fieldName = (field: QcLevelEditableField): string => `qc-level-${field}-${rowKey}-${index}`;

    return (
        <QcLevelLineLayout>
            <Input
                name={fieldName('name')}
                type="text"
                label={`${QC_CONFIG_KEY}.levels.level-name-label`}
                placeholder={`${QC_CONFIG_KEY}.levels.level-name-placeholder`}
                value={level.name}
                required
                onChange={(_, value) => onChange('name', value)}
            />
            <QcLevelRangeInputs>
                {resultType === 'QUALITATIVE' && (
                    <QcLevelTargetValues>
                        <Input
                            name={fieldName('defaultTargetValues')}
                            type="text"
                            label={`${QC_CONFIG_KEY}.levels.target-values-optional`}
                            ariaLabel={t(`${QC_CONFIG_KEY}.levels.target-values-aria`, { name: level.name })}
                            placeholder={`${QC_CONFIG_KEY}.levels.target-values-placeholder`}
                            pattern=".*"
                            value={level.defaultTargetValues ?? ''}
                            validity={errorKey ? { state: 'error', type: ['custom'] } : undefined}
                            validationMessages={errorKey ? { error: { custom: t(errorKey) } } : undefined}
                            onChange={(_, value) => onChange('defaultTargetValues', value)}
                        />
                    </QcLevelTargetValues>
                )}
                {resultType === 'QUANTITATIVE' &&
                    (rangeMode === 'MIN_MAX' ? (
                        <>
                            <Input
                                name={fieldName('defaultMin')}
                                type="number"
                                label={`${QC_CONFIG_KEY}.levels.min-optional`}
                                placeholder={`${QC_CONFIG_KEY}.levels.min`}
                                value={toInputValue(level.defaultMin)}
                                onChange={(_, value) => onChange('defaultMin', value)}
                            />
                            <Input
                                name={fieldName('defaultMax')}
                                type="number"
                                label={`${QC_CONFIG_KEY}.levels.max-optional`}
                                placeholder={`${QC_CONFIG_KEY}.levels.max`}
                                value={toInputValue(level.defaultMax)}
                                onChange={(_, value) => onChange('defaultMax', value)}
                            />
                        </>
                    ) : (
                        <>
                            <Input
                                name={fieldName('defaultTarget')}
                                type="number"
                                label={`${QC_CONFIG_KEY}.levels.target-optional`}
                                placeholder={`${QC_CONFIG_KEY}.levels.target-placeholder`}
                                value={toInputValue(level.defaultTarget)}
                                onChange={(_, value) => onChange('defaultTarget', value)}
                            />
                            <Input
                                name={fieldName('defaultDeviationPercent')}
                                type="number"
                                label={`${QC_CONFIG_KEY}.levels.deviation-optional`}
                                placeholder={`${QC_CONFIG_KEY}.levels.deviation-placeholder`}
                                value={toInputValue(level.defaultDeviationPercent)}
                                minValue={0}
                                onChange={(_, value) => onChange('defaultDeviationPercent', value)}
                            />
                        </>
                    ))}
            </QcLevelRangeInputs>
            <PocIconButton
                icon="delete"
                family="outlined"
                aria-label={t(`${QC_CONFIG_KEY}.levels.remove-aria`, { name: level.name })}
                data-testid={`qc-level-remove-${rowKey}-${index}`}
                onClick={onRemove}
            />
        </QcLevelLineLayout>
    );
};
